import './AddInputModal.scss';
import * as uuid from 'uuid';
import { useCallback, useMemo, useState, SetStateAction, Dispatch } from 'react';
import { Form, InputGroup, Modal } from 'react-bootstrap';
import { isAfter, isBefore, isEqual } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { MenuItem, TimelineEntry } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Intent } from '@Components/designConstants';
import {
  DEFAULT_MAX_YEAR,
  DEFAULT_MIN_YEAR,
  FOCUS_OPTIONS
} from '@Components/DatePicker/DatePickerButton/DatePickerConstants';
import { DPCalendar } from '@Components/DatePicker/DPCalendar';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';
import { EditableDropdown } from '@Components/EditableDropdown';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { DateRange } from '../../../utils/date-utils';
import { useSetAreYouSure } from '../../../redux/modals';
import { invalidOfflineInputsDates } from './OfflineInputsUtils';

interface AddPromoModalProps {
  show: boolean;
  handleClose: () => void;
  stageEntry: (input: TimelineEntry) => void;
  promos: MenuItem[];
  setPromos: (promos: MenuItem[]) => void;
  offers: MenuItem[];
  setOffers: (offers: MenuItem[]) => void;
  userFullName: string;
}

const disabledDeleteTooltipText = (field: string) =>
  `${field} already committed to the backend and can’t be deleted. Contact us if it needs to be removed.`;

export const AddPromoDataModal = ({
  show,
  handleClose,
  stageEntry,
  promos,
  setPromos,
  offers,
  setOffers,
  userFullName
}: AddPromoModalProps) => {
  const [focus, setFocus] = useState<(typeof FOCUS_OPTIONS)[number]>(FOCUS_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<MenuItem | undefined>();
  const [percentageDiscountChecked, setPercentageDiscountChecked] = useState<boolean>(false);
  const [percentageDiscount, setPercentageDiscount] = useState<number | null>(null);
  const [specialOfferChecked, setSpecialOfferChecked] = useState<boolean>(false);
  const [selectedOffer, setSelectedOffer] = useState<MenuItem | undefined>();
  const [dateRange, setDateRange] = useState<Partial<DateRange>>();
  const [showAddOfferModal, setShowAddOfferModal] = useState<boolean>(false);
  const [showAddPromoModal, setShowAddPromoModal] = useState<boolean>(false);
  const setAreYouSure = useSetAreYouSure();

  const today = formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd');

  const resetState = useCallback(() => {
    setIsSubmitting(false);
    setPercentageDiscountChecked(false);
    setPercentageDiscount(null);
    setSpecialOfferChecked(false);
    setDateRange(undefined);
    setShowAddOfferModal(false);
    setShowAddPromoModal(false);
    setSelectedOffer(undefined);
    setSelectedPromo(undefined);
  }, []);

  const isSubmitDisabled = useMemo(
    () =>
      isSubmitting ||
      !selectedPromo ||
      (!percentageDiscount && !selectedOffer) ||
      !dateRange?.start ||
      !dateRange?.end ||
      isAfter(new Date(dateRange.start), new Date(dateRange.end)),
    [isSubmitting, selectedPromo, percentageDiscount, selectedOffer, dateRange]
  );

  const onCalendarChange = useCallback(
    (newDateRange: Partial<DateRange>, newFocus: (typeof FOCUS_OPTIONS)[number]) => {
      setDateRange(newDateRange);
      setFocus(newFocus);
    },
    []
  );

  const addDropdownItem = useCallback(
    (
      label: string,
      field: string,
      itemArr: MenuItem[],
      setItemArr: (item: MenuItem[]) => void,
      setSelectedItem: (value: SetStateAction<MenuItem | undefined>) => void,
      setShowModal: Dispatch<SetStateAction<boolean>>,
      setSelectedItemChecked?: Dispatch<SetStateAction<boolean>>
    ) => {
      const newItem = {
        id: -Date.now(),
        label,
        updatedAt: today,
        updatedBy: userFullName,
        isDeletable: true
      };
      const addItem = () => {
        setItemArr([...itemArr, newItem]);
        setSelectedItem(newItem);
        if (setSelectedItemChecked) {
          setSelectedItemChecked(true);
        }
        setShowModal(false);
      };
      if (itemArr.some(entry => entry.label === label)) {
        setShowModal(false);
        setAreYouSure({
          title: `${label} ${field} already exists`,
          message: `Adding duplicate ${field}s can break our modeling. Please consider using the existing entry or a different name.`,
          intent: Intent.WARNING,
          cancelText: 'Cancel',
          okayText: 'Okay',
          promise: false,
          onOkay: addItem
        });
        return;
      }
      addItem();
    },
    [setAreYouSure, today, userFullName]
  );

  const deleteItem = useCallback(
    (
      item: MenuItem,
      itemArr: MenuItem[],
      setItemArr: (item: MenuItem[]) => void,
      setSelectedItem: (value: SetStateAction<MenuItem | undefined>) => void,
      field: string
    ) => {
      setAreYouSure({
        title: `Delete ${field}`,
        message: `You are about to delete this ${field}, are you sure?`,
        intent: Intent.ERROR,
        cancelText: 'Never mind',
        okayText: 'Yes',
        promise: false,
        onOkay: () => {
          if (item) {
            setItemArr(itemArr.filter(ele => ele.id !== item.id));
            setSelectedItem(undefined);
          }
        }
      });
    },
    [setAreYouSure]
  );

  const addModal = (
    field: string,
    showModal: boolean,
    setShowModal: Dispatch<SetStateAction<boolean>>,
    itemArr: MenuItem[],
    setItemArr: (item: MenuItem[]) => void,
    setSelectedItem: Dispatch<SetStateAction<MenuItem | undefined>>,
    setSelectedItemChecked?: Dispatch<SetStateAction<boolean>>
  ) =>
    showModal && (
      <RenameModal
        applyOnClick={label => {
          addDropdownItem(
            label,
            field,
            itemArr,
            setItemArr,
            setSelectedItem,
            setShowModal,
            setSelectedItemChecked
          );
        }}
        applyText='Save'
        cancelText='Discard'
        onCancel={() => {
          setShowModal(false);
        }}
        onHide={() => setShowModal(false)}
        placeholder={`New ${field} Name`}
        subTitle={`New ${field} Name`}
        title={`Add New ${field}`}
      />
    );

  const addPromoModal = addModal(
    'Promo',
    showAddPromoModal,
    setShowAddPromoModal,
    promos,
    setPromos,
    setSelectedPromo
  );

  const addOfferModal = addModal(
    'Offer',
    showAddOfferModal,
    setShowAddOfferModal,
    offers,
    setOffers,
    setSelectedOffer,
    setSpecialOfferChecked
  );

  return (
    <Modal
      className='add-input-modal add-promo-modal'
      show={show}
      onHide={() => {
        handleClose();
        resetState();
      }}
      size='xl'
      centered
    >
      {/* Modals */}
      {addPromoModal}
      {addOfferModal}
      <Modal.Header>
        <h4 className='modal-title'>Add Promo</h4>
        <div className='sub-title'>*Required fields marked with an asterisk.</div>
      </Modal.Header>
      <Modal.Body>
        <div className='promo-modal-column' id='col1'>
          <div className='promo-name-input'>
            <div className='section-title'>
              Promo Name<span className='asterisk'>*</span>
            </div>
            <EditableDropdown
              className='promo-dropdown'
              menuItems={promos}
              addItemText='Add Promo'
              dropdownLabel='Select Promo'
              maxLabelLength={200}
              deleteItemOnClick={(item: MenuItem) => {
                setSelectedPromo(item);
                deleteItem(item, promos, setPromos, setSelectedPromo, 'Promo');
              }}
              addItemOnClick={() => setShowAddPromoModal(true)}
              selectedItem={selectedPromo}
              setSelectedItem={setSelectedPromo}
              disabledDeleteTooltipText={disabledDeleteTooltipText('Promo')}
            />
            <div className='input-explanation'>
              <p>
                Important: Please use consistent promo names to ensure accurate tracking and
                reporting.
              </p>
            </div>
          </div>
        </div>
        <div className='promo-modal-column' id='col2'>
          <div className='promo-type-input'>
            <div className='section-title'>
              Promotion Type<span className='asterisk'>*</span>
            </div>
            <div className='percentage'>
              <div className='checkbox'>
                <Checkbox
                  label='Percentage'
                  checked={percentageDiscountChecked}
                  onChange={e => {
                    setPercentageDiscountChecked(!percentageDiscountChecked);
                    if (!e.target.checked) {
                      setPercentageDiscount(null);
                    }
                  }}
                />
              </div>
              <div className='input-and-explanation'>
                <InputGroup>
                  <Form.Control
                    type='number'
                    min={0}
                    max={100}
                    placeholder='ex: 55%'
                    value={
                      percentageDiscount !== null &&
                      percentageDiscount > 0 &&
                      percentageDiscount <= 100
                        ? percentageDiscount
                        : ''
                    }
                    onChange={e => {
                      const parsed = Number.parseFloat(e.target.value);
                      const enteredPercentage = Number.isNaN(parsed)
                        ? null
                        : Math.min(100, Math.max(0, parsed));
                      setPercentageDiscount(enteredPercentage);
                      setPercentageDiscountChecked(Boolean(enteredPercentage));
                    }}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
                <div className='input-explanation'>
                  <p>Must be less than or equal to 100%.</p>
                  <p>
                    Important: If multiple percentage off offers occur during the same period,
                    please enter each as a separate promo.
                  </p>
                </div>
              </div>
            </div>
            <div className='special-offer'>
              <div className='checkbox'>
                <Checkbox
                  label='Special Offer'
                  checked={specialOfferChecked}
                  onChange={e => {
                    setSpecialOfferChecked(!specialOfferChecked);
                    if (!e.target.checked) {
                      setSelectedOffer(undefined);
                    }
                  }}
                />
              </div>
              <div className='input-and-explanation'>
                <EditableDropdown
                  menuItems={offers}
                  addItemText='Add Offer'
                  dropdownLabel='Select Offer'
                  maxLabelLength={20}
                  deleteItemOnClick={(item: MenuItem) => {
                    setSelectedOffer(item);
                    deleteItem(item, offers, setOffers, setSelectedOffer, 'Offer');
                  }}
                  addItemOnClick={() => setShowAddOfferModal(true)}
                  selectedItem={selectedOffer}
                  setSelectedItem={(offer: MenuItem | undefined) => {
                    setSelectedOffer(offer);
                    setSpecialOfferChecked(Boolean(offer));
                  }}
                  disabledDeleteTooltipText={disabledDeleteTooltipText('Offer')}
                />
                <div className='input-explanation'>
                  <p>
                    Important: If multiple special offers occur during the same period, please enter
                    each as a separate promo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='promo-modal-column' id='col3'>
          <div className='time-frame'>
            <div className='section-title'>
              Time Frame<span className='asterisk'>*</span>
            </div>
            <div className='top-container'>
              <div className='date-container'>
                <DateInputField
                  label='Start Date'
                  value={dateRange?.start || ''}
                  setValue={newStartDate => {
                    setDateRange(prev => ({
                      ...prev,
                      start: newStartDate
                    }));
                  }}
                  invalidateDateOrRange={invalidOfflineInputsDates}
                  customValidation={[
                    {
                      validate: value => {
                        const parsedDate = new Date(value);
                        if (!dateRange?.end) {
                          return true;
                        }
                        return (
                          isBefore(parsedDate, new Date(dateRange.end)) ||
                          isEqual(parsedDate, new Date(dateRange.end))
                        );
                      },
                      errorMessage: 'Start date must be before end date.'
                    }
                  ]}
                />
                <DateInputField
                  label='End Date'
                  value={dateRange?.end || ''}
                  setValue={newEndDate => {
                    setDateRange(prev => ({
                      ...prev,
                      end: newEndDate
                    }));
                  }}
                  invalidateDateOrRange={invalidOfflineInputsDates}
                  customValidation={[
                    {
                      validate: value => {
                        const parsedDate = new Date(value);
                        if (!dateRange?.start) {
                          return true;
                        }
                        return (
                          isAfter(parsedDate, new Date(dateRange.start)) ||
                          isEqual(parsedDate, new Date(dateRange.start))
                        );
                      },
                      errorMessage: ''
                    }
                  ]}
                />
              </div>
            </div>
            <div className='bottom-container'>
              <DPCalendar
                start={dateRange?.start}
                end={dateRange?.end}
                focus={focus}
                onChange={onCalendarChange}
                years={Array.from(
                  { length: DEFAULT_MAX_YEAR + 1 - DEFAULT_MIN_YEAR },
                  (_, i) => DEFAULT_MIN_YEAR + i
                )}
                currentMonthSecond={false}
                numVisibleMonths={2}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={() => {
            handleClose();
            resetState();
          }}
        >
          Cancel
        </Button>
        <Button
          loading={isSubmitting}
          onClick={async () => {
            if (
              !dateRange?.start ||
              !dateRange.end ||
              !selectedPromo ||
              (!selectedOffer && !percentageDiscount) ||
              isSubmitDisabled
            ) {
              return;
            }
            setIsSubmitting(true);
            await stageEntry({
              promoPeriodId: uuid.v4(),
              promoName: selectedPromo.label,
              percentageDiscount,
              specialOfferName: selectedOffer?.label || null,
              startDate: dateRange.start,
              endDate: dateRange.end
            });
            handleClose();
            resetState();
          }}
          disabled={isSubmitDisabled}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
